import React, { useMemo, useCallback, useState } from 'react';
import { Box, Chip } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import { useTranslation } from 'react-i18next';
import { WorkflowPage, LeaveRequestViewModal } from '../../../components';
import { useEntityAlertMap } from '../../../hooks/useEntityAlertMap';
import { FORM_CONFIGS } from '../../../constants/formConfigurations';
import { TABLE_COLUMNS } from '../../../constants/tableConfigurations';
import { FILTER_CONFIGS } from '../../../constants/tableConfigurations';
import { SEARCH_CONFIGS } from '../../../constants/tableConfigurations';
import { profileLeavesTabStyles } from '../../ProfilePage/tabs/ProfileLeavesTab.styles';
import { useLeaveRequestsAdmin } from '../../../hooks/useLeaveRequestsAdmin';
import { useAuth, useLeaveRequestPermissions, SCREENS } from '../../../hooks';
import { useUsers } from '../../../hooks/useUsers';
import { useLeaveTypes } from '../../../hooks/useLeaveTypes';
import { useNotification } from '../../../hooks/useNotification';
import { hasuraClient } from '../../../utils/graphqlClient';
import {
  CREATE_LEAVE_REQUEST_MUTATION,
  DELETE_LEAVE_REQUEST_MUTATION,
} from '../../../graphql/mutations/leaveRequests';
import { calculateDuration } from '../../../utils/dateUtils';

const LeaveRequestsAdminTab = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const leaveRequestPermissions = useLeaveRequestPermissions();
  const { getRowSx } = useEntityAlertMap('leave');
  const {
    leaveRequests,
    loading,
    fetchData: adminFetchData,
    handleApprove,
    handleReject,
    serverPagination,
    page,
    pageSize,
    totalCount,
    hasNextPage,
    setPage,
    setPageSize,
    setExtraWhereCondition,
  } = useLeaveRequestsAdmin();
  const { users } = useUsers();
  const { leaveTypes } = useLeaveTypes();
  const { showNotification } = useNotification();
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [selectedLeaveRequest, setSelectedLeaveRequest] = useState(null);

  const filterConfigs = useMemo(() => {
    const allFilters = FILTER_CONFIGS.leaves;
    if (!user?.role) return [];

    // Inject the full leave-type list as filter options so users can pick a
    // type that isn't present in the current visible page (auto-generated
    // options from DataTable only see the loaded slice → misses Doğum Günü,
    // Mazeret etc. when server pagination is on).
    const typeOptions = Array.isArray(leaveTypes)
      ? leaveTypes.map((lt) => ({
          value: lt.leave_type_name,
          label: lt.leave_type_name,
        }))
      : [];
    const enrich = (filter) => {
      if (filter.field === 'leaveTypeName' && typeOptions.length > 0) {
        return { ...filter, options: typeOptions };
      }
      return filter;
    };

    let filtered = allFilters;
    if (user?.isLoanRequestManager !== true) {
      filtered = allFilters.filter(filter => {
        const hasRoleFlags =
          filter.showIfRoot ||
          filter.showIfDirector ||
          filter.showIfGroupManager;
        if (!hasRoleFlags) return true;
        if (user.role === 'ROOT') return filter.showIfRoot;
        if (user.role === 'DIRECTOR') return filter.showIfDirector;
        if (user.role === 'GROUP_MANAGER') return filter.showIfGroupManager;
        return false;
      });
    }
    return filtered.map(enrich);
  }, [user?.role, user?.isLoanRequestManager, leaveTypes]);

  const canAddLeave = leaveRequestPermissions.canCreate;

  // Helper function to check if a leave type is hourly
  const isHourlyLeaveType = useCallback(
    leaveTypeId => {
      if (!leaveTypeId || !Array.isArray(leaveTypes)) return false;
      const id = parseInt(leaveTypeId, 10);
      const lt = leaveTypes.find(x => x.id === id);
      if (!lt) return false;

      const name = lt.leave_type_name?.toLowerCase().trim() || '';
      const days = lt.leave_type_days ?? 0;
      const hours = lt.leave_type_hours ?? 0;

      if (hours > 0 && (!days || days === 0)) return true;
      if (
        name.includes('saatlik') ||
        name.includes('hourly') ||
        name.includes(' saat ')
      ) {
        return true;
      }
      return false;
    },
    [leaveTypes]
  );

  const formFields = useMemo(() => {
    let fields = [...FORM_CONFIGS.leaves.fields];
    if (canAddLeave) {
      let userOptions = [];
      if (user?.role === 'ROOT') {
        userOptions = users.map(u => ({
          value: u.id,
          label: `${u.firstName} ${u.lastName}`,
        }));
      } else if (user?.role === 'DIRECTOR') {
        userOptions = users
          .filter(u => u.companyId === user.companyId)
          .map(u => ({ value: u.id, label: `${u.firstName} ${u.lastName}` }));
      }

      fields = [
        {
          name: 'userId',
          type: 'select',
          label: 'leaves.form.user',
          required: true,
          fullWidth: true,
          options: userOptions,
          validation: {
            required: true,
            requiredMessage: t('leaves.form.validation.userRequired'),
          },
        },
        ...fields,
      ];
    }

    const shouldShowBirthdayLeave = selectedUserId => {
      if (!selectedUserId) return false;

      const selectedUser = users.find(
        u => u.id === parseInt(selectedUserId, 10)
      );
      if (!selectedUser?.birthDate) return false;

      const today = new Date();
      const currentMonth = today.getMonth();

      const birthDate = new Date(selectedUser.birthDate);
      const birthMonth = birthDate.getMonth();

      const nextMonth = (currentMonth + 1) % 12;
      return currentMonth === birthMonth || nextMonth === birthMonth;
    };

    fields = fields.map(field => {
      if (field.name === 'leaveType') {
        return {
          ...field,
          options: formData => {
            const shouldShowBirthdayLeave = selectedUserId => {
              if (!selectedUserId) return false;

              const selectedUser = users.find(
                u => u.id === parseInt(selectedUserId, 10)
              );
              if (!selectedUser?.birthDate) return false;

              const today = new Date();
              const currentMonth = today.getMonth(); // 0-11

              const birthDate = new Date(selectedUser.birthDate);
              const birthMonth = birthDate.getMonth(); // 0-11

              const nextMonth = (currentMonth + 1) % 12;
              return currentMonth === birthMonth || nextMonth === birthMonth;
            };

            const selectedUserId = formData?.userId;

            return leaveTypes
              .filter(lt => {
                const isBirthdayLeave =
                  lt.leave_type_name?.toLowerCase().includes('doğum') ||
                  lt.leave_type_name?.toLowerCase().includes('birthday') ||
                  lt.leave_type_name?.toLowerCase().includes('dogum');

                if (isBirthdayLeave) {
                  return shouldShowBirthdayLeave(selectedUserId);
                }

                return true;
              })
              .map(lt => ({ value: lt.id, label: lt.leave_type_name }));
          },
        };
      }

      if (field.name === 'startDate' || field.name === 'endDate') {
        return {
          ...field,
          showTime: formData => isHourlyLeaveType(formData?.leaveType),
        };
      }
      return field;
    });
    return fields;
  }, [
    users,
    canAddLeave,
    user?.role,
    user?.companyId,
    leaveTypes,
    t,
    isHourlyLeaveType,
  ]);

  const statusCounts = useMemo(() => {
    if (!Array.isArray(leaveRequests))
      return { all: 0, PENDING: 0, APPROVED: 0, REJECTED: 0 };
    const counts = {
      all: leaveRequests.length,
      PENDING: 0,
      APPROVED: 0,
      REJECTED: 0,
    };
    leaveRequests.forEach(lr => {
      if (counts.hasOwnProperty(lr.status)) counts[lr.status]++;
    });
    return counts;
  }, [leaveRequests]);

  const handleFormSubmit = async (formData) => {
    try {
      const selectedUser = users.find(
        u => u.id === parseInt(formData.userId, 10)
      );

      // Check if it's an hourly leave type
      const isHourly = isHourlyLeaveType(formData.leaveType);

      const payload = {
        user_id: formData.userId,
        company_id: selectedUser?.companyId,
        team_id: selectedUser?.teamId || null,
        group_id: selectedUser?.team?.group_id || null,
        start_date: formData?.startDate,
        end_date: formData?.endDate,
        duration: calculateDuration(
          formData?.startDate,
          formData?.endDate,
          isHourly
        ),
        leave_type: formData.leaveType,
        leave_request_description: formData.leave_request_description || null,
      };

      await hasuraClient.mutate(CREATE_LEAVE_REQUEST_MUTATION, {
        object: {
          ...payload,
          status: 'PENDING',
        },
      });
      await adminFetchData();
      showNotification(t('hooks.leaves.success.created'), 'success');
      return { success: true };
    } catch (error) {
      showNotification(
        t('hooks.leaves.error.create', 'İşlem sırasında bir hata oluştu.'),
        'error'
      );
      return {
        success: false,
        error: error.message || t('hooks.leaves.error.create'),
      };
    }
  };

  const handleDeleteLeave = async row => {
    // Her türlü silme işlemi sadece ROOT
    if (user?.role !== 'ROOT') {
      showNotification(
        t(
          'hooks.leaves.error.onlyRootCanDelete',
          'İzin kayıtlarını sadece ROOT silebilir.'
        ),
        'error'
      );
      return;
    }

    try {
      await hasuraClient.mutate(DELETE_LEAVE_REQUEST_MUTATION, {
        id: row.id,
      });
      await adminFetchData();
      showNotification(
        t('hooks.leaves.success.deleted', 'İzin kaydı silindi.'),
        'success'
      );
    } catch (error) {
      showNotification(
        t(
          'hooks.leaves.error.delete',
          'İzin kaydı silinirken bir hata oluştu.'
        ),
        'error'
      );
      throw error;
    }
  };

  const handleApproveWithNotification = async leaveRequestId => {
    try {
      await handleApprove(leaveRequestId);
      showNotification(t('leaves.actions.approved'), 'success');
    } catch (error) {
      showNotification(t('leaves.actions.approveError'), 'error');
      throw error;
    }
  };

  const handleRejectWithNotification = async (leaveRequestId, reason) => {
    try {
      await handleReject(leaveRequestId, reason);
      showNotification(t('leaves.actions.rejected'), 'success');
    } catch (error) {
      showNotification(t('leaves.actions.rejectError'), 'error');
      throw error;
    }
  };

  const columns = useMemo(
    () =>
      TABLE_COLUMNS.leavesRequest.map(col =>
        col.field === 'status'
          ? {
              ...col,
              render: (value, row) => {
                // Final status'lar
                if (value === 'REJECTED') {
                  return (
                    <Box
                      component="span"
                      sx={{ display: 'inline-block', pointerEvents: 'none' }}
                    >
                      <Chip
                        label={t('leaves.status.rejected')}
                        color="error"
                        size="small"
                        sx={profileLeavesTabStyles.statusChip('REJECTED')}
                      />
                    </Box>
                  );
                }
                if (value === 'APPROVED') {
                  return (
                    <Box
                      component="span"
                      sx={{ display: 'inline-block', pointerEvents: 'none' }}
                    >
                      <Chip
                        label={t('leaves.status.approved')}
                        color="success"
                        size="small"
                        sx={profileLeavesTabStyles.statusChip('APPROVED')}
                      />
                    </Box>
                  );
                }

                // Approval workflow status'ları
                if (row.is_group_manager_approved === false) {
                  return (
                    <Box
                      component="span"
                      sx={{ display: 'inline-block', pointerEvents: 'none' }}
                    >
                      <Chip
                        label={t('leaves.status.groupRejected')}
                        color="error"
                        size="small"
                        sx={profileLeavesTabStyles.statusChip('GROUP_REJECTED')}
                      />
                    </Box>
                  );
                }
                if (row.is_team_manager_approved === false) {
                  return (
                    <Box
                      component="span"
                      sx={{ display: 'inline-block', pointerEvents: 'none' }}
                    >
                      <Chip
                        label={t('leaves.status.teamRejected')}
                        color="error"
                        size="small"
                        sx={profileLeavesTabStyles.statusChip('TEAM_REJECTED')}
                      />
                    </Box>
                  );
                }
                if (row.is_group_manager_approved === true) {
                  return (
                    <Box
                      component="span"
                      sx={{ display: 'inline-block', pointerEvents: 'none' }}
                    >
                      <Chip
                        label={t('leaves.status.groupApproved')}
                        color="info"
                        size="small"
                        sx={profileLeavesTabStyles.statusChip('GROUP_APPROVED')}
                      />
                    </Box>
                  );
                }
                if (row.is_team_manager_approved === true) {
                  return (
                    <Box
                      component="span"
                      sx={{ display: 'inline-block', pointerEvents: 'none' }}
                    >
                      <Chip
                        label={t('leaves.status.teamApproved')}
                        color="warning"
                        size="small"
                        sx={profileLeavesTabStyles.statusChip('TEAM_APPROVED')}
                      />
                    </Box>
                  );
                }

                // Default PENDING status
                return (
                  <Box
                    component="span"
                    sx={{ display: 'inline-block', pointerEvents: 'none' }}
                  >
                    <Chip
                      label={t(`leaves.status.${value?.toLowerCase?.()}`)}
                      sx={profileLeavesTabStyles.statusChip(value)}
                      size="small"
                      clickable={false}
                      component="span"
                      tabIndex={-1}
                      onKeyDown={e => e.preventDefault()}
                      onClick={e => e.preventDefault()}
                    />
                  </Box>
                );
              },
            }
          : col
      ),
    [t]
  );

  const handleViewLeaveRequest = useCallback(row => {
    setSelectedLeaveRequest(row);
    setViewModalOpen(true);
  }, []);

  const handleCloseViewModal = useCallback(() => {
    setViewModalOpen(false);
    setSelectedLeaveRequest(null);
  }, []);

  const handlePrintLeaveRequest = useCallback(row => {
    if (!row || row.status !== 'APPROVED') return;

    const url = `/applications/leave-requests/print/${row.id}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }, []);

  const rowActions = useCallback(
    row => {
      const actions = [];

      // View action - always available
      actions.push({
        type: 'view',
        label: 'table.actions.view',
        color: 'info',
        icon: <VisibilityIcon fontSize="small" />,
      });

      // Print action - only for approved leaves
      if (row.status === 'APPROVED') {
        actions.push({
          type: 'print',
          label: 'leaves.actions.print',
          color: 'primary',
          icon: <PrintIcon fontSize="small" />,
        });
      }

      // Approve/Reject actions - only for pending requests
      if (row.status !== 'APPROVED' && row.status !== 'REJECTED') {
        // LoanRequestManager or user with canEdit permission
        const canApproveReject =
          user?.isLoanRequestManager === true ||
          leaveRequestPermissions.canEdit;
        if (canApproveReject) {
          actions.push({
            type: 'approve',
            label: 'leaves.actions.approve',
            color: 'success',
            icon: <CheckCircleIcon fontSize="small" />,
          });
          actions.push({
            type: 'reject',
            label: 'leaves.actions.reject',
            color: 'error',
            icon: <CancelIcon fontSize="small" />,
          });
        }
      }

      // Edit/Delete – sadece ROOT kullanıcıları (her statüde)
      if (user?.role === 'ROOT') {
        actions.push({
          type: 'delete',
          label: 'table.actions.delete',
          color: 'error',
        });
      }

      return actions;
    },
    [ leaveRequestPermissions.canDelete, user?.isLoanRequestManager, user?.role]
  );

  const handleRowAction = useCallback(
    (actionType, row) => {
      if (actionType === 'view') {
        handleViewLeaveRequest(row);
        return true; // Prevent default action
      }
      if (actionType === 'print') {
        handlePrintLeaveRequest(row);
        return true;
      }
      return false; // Allow default action for approve/reject
    },
    [handleViewLeaveRequest, handlePrintLeaveRequest]
  );

  const pageConfig = useMemo(
    () => ({
      columns,
      filterConfigs,
      searchConfig: SEARCH_CONFIGS.leavesRequest,
      formConfig: {
        fields: formFields,
        title: FORM_CONFIGS.leaves.title,
      },
    }),
    [columns, filterConfigs, formFields]
  );

  return (
    <>
      <WorkflowPage
        entityType="leaves"
        screen={SCREENS.LEAVE_REQUESTS}
        data={leaveRequests}
        loading={loading}
        columns={pageConfig.columns}
        formFields={pageConfig.formConfig.fields}
        filterConfigs={pageConfig.filterConfigs}
        searchConfig={pageConfig.searchConfig}
        formConfig={pageConfig.formConfig}
        statusCounts={statusCounts}
        onFormSubmit={handleFormSubmit}
        onApprove={handleApproveWithNotification}
        onReject={handleRejectWithNotification}
        rowActions={rowActions}
        onRowAction={handleRowAction}
        onDelete={handleDeleteLeave}
        canAdd={canAddLeave}
        addButtonText={t('leaves.actions.add')}
        addButtonIcon={<AddIcon />}
        currentPage={t('pages.leaveRequests')}
        showViewGuard={true}
        getRowSx={getRowSx}
        serverPagination={serverPagination}
        serverPage={page}
        serverPageSize={pageSize}
        serverTotalCount={totalCount}
        serverHasNextPage={hasNextPage}
        onServerPageChange={setPage}
        onServerPageSizeChange={setPageSize}
        setExtraWhereCondition={setExtraWhereCondition}
      />

      <LeaveRequestViewModal
        open={viewModalOpen}
        onClose={handleCloseViewModal}
        leaveRequest={selectedLeaveRequest}
      />
    </>
  );
};

export default LeaveRequestsAdminTab;
